import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  Package, Plus, Filter, Search, Users, Building2, Star, 
  TrendingUp, DollarSign, Clock, CheckCircle, XCircle, 
  AlertTriangle, ArrowUpRight, ArrowDownRight, Download,
  RefreshCw, Eye, Edit, MoreVertical, Calendar, MapPin,
  Truck, Globe, Shield, FileText, BarChart3, PieChart,
  Activity, ShoppingCart, CreditCard, Receipt
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';

const Procurement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [timeRange, setTimeRange] = useState('7d');
  const { isDarkMode } = useChainsTheme();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock data for procurement
  const procurementData = [
    { month: 'Jan', spend: 125000, orders: 45, savings: 18500, efficiency: 94.2, vendors: 12 },
    { month: 'Feb', spend: 138000, orders: 52, savings: 20800, efficiency: 95.8, vendors: 14 },
    { month: 'Mar', spend: 152000, orders: 58, savings: 22800, efficiency: 96.4, vendors: 15 },
    { month: 'Apr', spend: 168000, orders: 65, savings: 25200, efficiency: 97.1, vendors: 16 },
    { month: 'May', spend: 182000, orders: 72, savings: 27300, efficiency: 96.8, vendors: 18 },
    { month: 'Jun', spend: 195000, orders: 78, savings: 29250, efficiency: 97.3, vendors: 20 },
  ];

  const vendorData = [
    { 
      id: 'V001', 
      name: 'TechCorp Solutions', 
      category: 'IT Equipment', 
      rating: 4.8, 
      status: 'active', 
      totalOrders: 156, 
      totalSpent: 125000, 
      onTimeDelivery: 98, 
      qualityScore: 95, 
      lastOrder: '2024-01-15', 
      contactPerson: 'John Smith', 
      email: 'john@techcorp.com', 
      phone: '+966-50-123-4567',
      location: 'Riyadh',
      contractEnd: '2024-12-31',
      performance: 'excellent'
    },
    { 
      id: 'V002', 
      name: 'Global Logistics Co.', 
      category: 'Logistics', 
      rating: 4.6, 
      status: 'active', 
      totalOrders: 142, 
      totalSpent: 118000, 
      onTimeDelivery: 95, 
      qualityScore: 92, 
      lastOrder: '2024-01-14', 
      contactPerson: 'Sarah Johnson', 
      email: 'sarah@globallogistics.com', 
      phone: '+966-50-234-5678',
      location: 'Jeddah',
      contractEnd: '2024-11-30',
      performance: 'good'
    },
    { 
      id: 'V003', 
      name: 'Premium Suppliers', 
      category: 'Office Supplies', 
      rating: 4.7, 
      status: 'active', 
      totalOrders: 134, 
      totalSpent: 120000, 
      onTimeDelivery: 96, 
      qualityScore: 94, 
      lastOrder: '2024-01-13', 
      contactPerson: 'Mike Chen', 
      email: 'mike@premiumsuppliers.com', 
      phone: '+966-50-345-6789',
      location: 'Dammam',
      contractEnd: '2024-10-31',
      performance: 'excellent'
    },
    { 
      id: 'V004', 
      name: 'FastTrack Express', 
      category: 'Transportation', 
      rating: 4.4, 
      status: 'active', 
      totalOrders: 128, 
      totalSpent: 112000, 
      onTimeDelivery: 92, 
      qualityScore: 88, 
      lastOrder: '2024-01-12', 
      contactPerson: 'Lisa Rodriguez', 
      email: 'lisa@fasttrack.com', 
      phone: '+966-50-456-7890',
      location: 'Abha',
      contractEnd: '2024-09-30',
      performance: 'good'
    },
    { 
      id: 'V005', 
      name: 'Quality Materials Ltd.', 
      category: 'Raw Materials', 
      rating: 4.5, 
      status: 'review', 
      totalOrders: 98, 
      totalSpent: 89000, 
      onTimeDelivery: 89, 
      qualityScore: 91, 
      lastOrder: '2024-01-11', 
      contactPerson: 'David Kim', 
      email: 'david@qualitymaterials.com', 
      phone: '+966-50-567-8901',
      location: 'Tabuk',
      contractEnd: '2024-08-31',
      performance: 'average'
    }
  ];

  const purchaseOrders = [
    { 
      id: 'PO-2024-001', 
      vendor: 'TechCorp Solutions', 
      amount: 25000.00, 
      status: 'approved', 
      priority: 'high', 
      createdAt: '2024-01-15', 
      expectedDelivery: '2024-01-25', 
      items: 15, 
      description: 'IT equipment for new office setup',
      category: 'IT Equipment',
      approver: 'Ahmed Al-Rashid',
      paymentTerms: 'Net 30'
    },
    { 
      id: 'PO-2024-002', 
      vendor: 'Premium Suppliers', 
      amount: 8500.00, 
      status: 'pending', 
      priority: 'medium', 
      createdAt: '2024-01-14', 
      expectedDelivery: '2024-01-22', 
      items: 25, 
      description: 'Monthly office supplies',
      category: 'Office Supplies',
      approver: 'Sarah Johnson',
      paymentTerms: 'Net 15'
    },
    { 
      id: 'PO-2024-003', 
      vendor: 'Global Logistics Co.', 
      amount: 15000.00, 
      status: 'in_review', 
      priority: 'low', 
      createdAt: '2024-01-13', 
      expectedDelivery: '2024-01-30', 
      items: 8, 
      description: 'Logistics services for Q1',
      category: 'Logistics',
      approver: 'Mike Chen',
      paymentTerms: 'Net 45'
    },
    { 
      id: 'PO-2024-004', 
      vendor: 'FastTrack Express', 
      amount: 12000.00, 
      status: 'approved', 
      priority: 'high', 
      createdAt: '2024-01-12', 
      expectedDelivery: '2024-01-20', 
      items: 12, 
      description: 'Express delivery services',
      category: 'Transportation',
      approver: 'Lisa Rodriguez',
      paymentTerms: 'Net 30'
    }
  ];

  const performanceMetrics = [
    { name: 'Total Spend', value: 'SAR 325K', change: '+15%', trend: 'up', icon: DollarSign, color: 'blue' },
    { name: 'Active Vendors', value: '24', change: '+2', trend: 'up', icon: Users, color: 'green' },
    { name: 'Purchase Orders', value: '156', change: '+8%', trend: 'up', icon: ShoppingCart, color: 'purple' },
    { name: 'Cost Savings', value: 'SAR 45K', change: '+12%', trend: 'up', icon: TrendingUp, color: 'emerald' },
    { name: 'On-Time Delivery', value: '94.8%', change: '+2.1%', trend: 'up', icon: CheckCircle, color: 'green' },
    { name: 'Quality Score', value: '92.3%', change: '+1.5%', trend: 'up', icon: Star, color: 'yellow' }
  ];

  const categoryData = [
    { name: 'IT Equipment', value: 35, color: '#3B82F6' },
    { name: 'Logistics', value: 25, color: '#10B981' },
    { name: 'Office Supplies', value: 20, color: '#F59E0B' },
    { name: 'Transportation', value: 15, color: '#EF4444' },
    { name: 'Raw Materials', value: 5, color: '#8B5CF6' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'review': return 'text-orange-400';
      case 'inactive': return 'text-red-400';
      case 'approved': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'in_review': return 'text-blue-400';
      case 'rejected': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20';
      case 'review': return 'bg-orange-500/20';
      case 'inactive': return 'bg-red-500/20';
      case 'approved': return 'bg-green-500/20';
      case 'pending': return 'bg-yellow-500/20';
      case 'in_review': return 'bg-blue-500/20';
      case 'rejected': return 'bg-red-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const VendorCard = ({ vendor }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{vendor.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(vendor.status)} ${getStatusColor(vendor.status)}`}>
              {vendor.status}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{vendor.category} • {vendor.location}</p>
        </div>
        <div className="flex items-center space-x-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i < Math.floor(vendor.rating) ? 'text-yellow-400 fill-current' : 'text-gray-400'
              }`}
            />
          ))}
          <span className={`text-sm font-medium ml-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{vendor.rating}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Total Orders</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{vendor.totalOrders}</p>
        </div>
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Total Spent</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {vendor.totalSpent.toLocaleString()}</p>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>On-Time Delivery</span>
          <span className={`font-medium ${vendor.onTimeDelivery >= 95 ? 'text-green-400' : 'text-orange-400'}`}>
            {vendor.onTimeDelivery}%
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Quality Score</span>
          <span className={`font-medium ${vendor.qualityScore >= 90 ? 'text-green-400' : 'text-orange-400'}`}>
            {vendor.qualityScore}%
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Performance</span>
          <span className={`font-medium ${
            vendor.performance === 'excellent' ? 'text-green-400' :
            vendor.performance === 'good' ? 'text-blue-400' : 'text-orange-400'
          }`}>
            {vendor.performance}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="text-sm">
          <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Contact: {vendor.contactPerson}</p>
          <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Last Order: {vendor.lastOrder}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const PurchaseOrderCard = ({ po }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{po.id}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(po.status)} ${getStatusColor(po.status)}`}>
              {po.status.replace('_', ' ')}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{po.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Vendor</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{po.vendor}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Category</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{po.category}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Items</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{po.items}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Expected Delivery</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{po.expectedDelivery}</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="text-right">
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {po.amount.toLocaleString()}</p>
          <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Total Amount</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
                      <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Procurement Management</h1>
          <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-700'}`}>Vendor management, purchase orders, and procurement analytics</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <Download className="w-5 h-5 mr-2 inline" />
            Export
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            New Purchase Order
          </motion.button>
        </div>
      </motion.div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {performanceMetrics.map((metric, index) => (
          <motion.div
            key={metric.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`backdrop-blur-sm rounded-2xl p-6 ${
              isDarkMode 
                ? 'bg-white/10 border border-white/20' 
                : 'bg-white/70 border border-gray-200/50'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-${metric.color}-500/20`}>
                <metric.icon className={`w-6 h-6 text-${metric.color}-500`} />
              </div>
              <div className={`flex items-center text-sm ${
                metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
              }`}>
                {metric.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                )}
                {metric.change}
              </div>
            </div>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.value}</p>
            <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{metric.name}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Procurement Performance Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Procurement Performance</h3>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Spend</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Savings</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={procurementData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#f0f0f0'} />
              <XAxis dataKey="month" stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <YAxis stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
              <Bar dataKey="spend" fill="#3B82F6" fillOpacity={0.3} />
              <Line type="monotone" dataKey="savings" stroke="#10B981" strokeWidth={3} />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Category Distribution */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Spend by Category</h3>
            <button className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}>View All</button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {categoryData.map((category, index) => (
              <div key={category.name} className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>{category.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-1 p-1 backdrop-blur-sm rounded-xl border border-white/20">
        {[
          { id: 'overview', label: 'Overview', count: vendorData.length + purchaseOrders.length },
          { id: 'vendors', label: 'Vendors', count: vendorData.length },
          { id: 'purchase-orders', label: 'Purchase Orders', count: purchaseOrders.length },
          { id: 'contracts', label: 'Contracts', count: 12 }
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <span>{tab.label}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
              activeTab === tab.id ? 'bg-white/20' : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
            }`}>
              {tab.count}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Content based on active tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'vendors' && (
          <motion.div
            key="vendors"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {vendorData.map((vendor, index) => (
              <motion.div
                key={vendor.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <VendorCard vendor={vendor} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'purchase-orders' && (
          <motion.div
            key="purchase-orders"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {purchaseOrders.map((po, index) => (
              <motion.div
                key={po.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <PurchaseOrderCard po={po} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Vendors</h3>
              {vendorData.slice(0, 2).map((vendor, index) => (
                <motion.div
                  key={vendor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <VendorCard vendor={vendor} />
                </motion.div>
              ))}
            </div>
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Purchase Orders</h3>
              {purchaseOrders.slice(0, 2).map((po, index) => (
                <motion.div
                  key={po.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <PurchaseOrderCard po={po} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Procurement; 